import {
	type default as AnalyticsWebClient,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client';

import { type AnalyticsEventPayload, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { useAnalyticsAttributes } from './context';

type ErrorDetails = {
	message: string;
	stack?: string;
};

export type AnalyticsAttributes = Record<
	string,
	string | number | boolean | ErrorDetails | undefined | object
>;

interface FireAnalyticsProps {
	eventType: 'track' | 'ui' | 'operational';
	action: string;
	actionSubject: string;
	analyticsAttributes?: AnalyticsAttributes;
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
}

export const useFireAnalyticEvent = ({
	analyticsAttributes,
	eventType,
	action,
	createAnalyticsEvent,
	actionSubject,
}: FireAnalyticsProps): void => {
	const baseAnalyticsAttributes = useAnalyticsAttributes();
	const analyticsEvent = createAnalyticsEvent({
		action,
		actionSubject,
		eventType,
		tags: ['townsquare'],
		attributes: { ...analyticsAttributes, baseAnalyticsAttributes },
	});

	analyticsEvent.fire('atlas');
};

// TODO: replace all fireAnalyticEvent references with useFireAnalyticEvent https://product-fabric.atlassian.net/browse/TC-13334
export const fireAnalyticEvent = ({
	analyticsAttributes,
	eventType,
	action,
	createAnalyticsEvent,
	actionSubject,
}: FireAnalyticsProps): void => {
	const analyticsEvent = createAnalyticsEvent({
		action,
		actionSubject,
		eventType,
		tags: ['townsquare'],
		attributes: { ...analyticsAttributes },
	});

	analyticsEvent.fire('atlas');
};

/*
 * NOTE: at least one of accountId and cloudId must be provided in order for analytics
 * to be sent successfully.
 */
export function setAnalyticsClientInfo(
	analyticsClient: AnalyticsWebClient,
	subproduct: 'townsquare-goals' | 'townsquare-projects',
	accountId?: string,
	orgId?: string,
	cloudId?: string,
): void {
	analyticsClient.setSubproduct(subproduct);

	if (orgId && orgId !== '') {
		analyticsClient.setOrgInfo(orgId);
	}

	if (cloudId && cloudId !== '') {
		analyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	} else {
		analyticsClient.setTenantInfo(tenantType.NONE, cloudId);
	}

	if (accountId && accountId !== '') {
		analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
	}
}
